import React, { ClipboardEventHandler } from 'react';
import { v1 as uuid } from 'uuid';
import cloneDeep from 'lodash/cloneDeep';

import Chip from 'components/Chip';
import SelectLabel from './SelectLabel';
import SelectOptions from './SelectOptions';
import SelectActionsIcon from './SelectActionsIcon';
import {
  Wrapper,
  ChipsInput,
  ChipsWrapper,
  InputContainer,
  SingleValue,
} from './styles';
import APopover from '../APopover';
import { PopoverPlacement } from '../PopoverBase/types';
import i18n from '../../i18n';
import { preprocessForSearch } from '../../utils/number';

export interface IOption {
  value: number | string;
  label: string;
  parent?: number | string;
  [key: string]: any;
}

interface ISelectProps {
  onChangeValues: (values: IOption[]) => void;
  values: IOption[];
  options: IOption[];
  placeholder?: string;
  label?: string;
  multiChoice?: boolean;
  selectionIndicator?: boolean;
  letAddChip?: boolean;
  isRequired?: boolean;
  onSearch?: (searchTerm: string) => void;
  showValues?: boolean;
  disabled?: boolean;
  allowRemove?: boolean;
  round?: boolean;
  containerStyle?: any;
  inputStyle?: any;
  containerClass?: string;
  fixMisPositionError?: boolean;
  onPaste?: ClipboardEventHandler<HTMLInputElement>;
  placement?: PopoverPlacement;
  target?: React.ReactElement;
  arrow?: boolean;
}

interface ISelectState {
  focused: boolean;
  show: boolean;
  pointer: number;
  inputValue: string;
  selectedChip: IOption['value'];
  expanded: Set<number | string>;
  expandable: Set<number | string>;
}

class Select extends React.PureComponent<ISelectProps, ISelectState> {
  static defaultProps = {
    values: [],
    multiChoice: false,
    letAddChip: false,
    showValues: true,
  };

  state = {
    pointer: 0,
    focused: false,
    show: false,
    inputValue: '',
    selectedChip: -1,
    expanded: new Set<number | string>(),
    expandable: new Set<number | string>(),
  };
  timer: any;
  inputId = uuid();

  inputRef: React.RefObject<HTMLInputElement> = React.createRef();
  wrapperRef: React.RefObject<HTMLInputElement> = React.createRef();
  onPaste: ClipboardEventHandler<HTMLInputElement> = (event) => {
    const pastedText = event.clipboardData.getData('text/plain');
    const parts = pastedText.split(/\n+|,/);
    if (parts.length > 1) {
      const filteredOptions = this.getFilteredOptions();
      this.addItem(filteredOptions, parts);
      event.preventDefault();
    }
  };

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside, false);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside, false);
  }

  handleClickOutside = (event: any) => {
    if (
      this.wrapperRef.current &&
      !this.wrapperRef.current.contains(event.target)
    ) {
      this.setState({ focused: false });
    }
  };

  getFilteredOptions: () => IOption[] = () => {
    return this.props.options.filter((option) => {
      if (this.props.onSearch) {
        return true;
      }
      return preprocessForSearch(option.label).includes(
        preprocessForSearch(this.state.inputValue)
      );
    });
    // .filter((option) => {
    //   if (this.props.multiChoice) {
    //     return !this.props.values.find((item) => item.value === option.value);
    //   }
    //   return true;
    // });
  };

  handleActionsBasedOnKey = (e: React.KeyboardEvent) => {
    const key = e.which || e.keyCode || 0;
    if (key === 13 || key === 38 || key === 40) {
      e.preventDefault();
    }
    const pointer = this.state.pointer;
    const filteredOptions = this.getFilteredOptions();
    if (key === 13 && this.state.inputValue.length > 0) {
      // user enters ENTER KEY and input is not empty
      this.addItem(filteredOptions, [this.state.inputValue]);
    } else if (key === 13 && pointer >= 0) {
      // enter on a option by its pointer
      const option = filteredOptions[pointer];
      // check pointer not out of filteredOptions index
      if (option) {
        this.handleAddOption(option as IOption);
      }
    } else if (key === 38 && pointer > 0) {
      // arrow up
      this.setState((prevState) => ({ pointer: prevState.pointer - 1 }));
    } else if (key === 40 && pointer < filteredOptions.length - 1) {
      // arrow down
      this.setState((prevState) => ({ pointer: prevState.pointer + 1 }));
    } else if (key === 8 && this.state.inputValue.length === 0) {
      // user enters BACKSPACE KEY and input is empty
      if (this.state.selectedChip === -1) {
        // if user enters BACKSPACE KEY, select last chip
        const lastChip = this.props.values.slice(-1).pop();
        this.setState({ selectedChip: lastChip ? lastChip.value : -1 });
      } else {
        // if user enters BACKSPACE KEY to remove selected chip
        const targetedChip = this.props.values.find(
          (item) => item.value === this.state.selectedChip
        );
        if (targetedChip) {
          const values = this.props.values.filter(
            (item) => item.value !== targetedChip.value
          );
          this.props.onChangeValues(values);
        }
        this.setState({ selectedChip: -1 });
      }
    } else {
      this.setState({ selectedChip: -1 });
    }
  };

  expand = (value: string | number, expand: boolean = true) => {
    const expanded = cloneDeep(this.state.expanded);
    if (expand) {
      expanded.add(value);
    } else {
      expanded.delete(value);
    }
    this.setState({ expanded });
  };

  addItem = (filteredOptions: IOption[], items: string[]) => {
    const values = cloneDeep(this.props.values);
    items.forEach((input: string) => {
      input = input.trim();
      const firstMatched = filteredOptions.find((item) => {
        return item.label.includes(input);
      });
      if (firstMatched) {
        values.push(firstMatched);
        this.setState({ inputValue: '' });
      } else if (this.props.letAddChip) {
        // if filtered options is empty and select is letAddChip we add it to values
        if (!values.find((item) => item.value === input)) {
          // don't let user to add duplicate chip
          const newValue: IOption = {
            label: input,
            value: input,
          };
          values.push(newValue);
          this.setState({ inputValue: '' });
        } else {
          this.setState({ inputValue: '' });
        }
      }
    });

    this.props.onChangeValues(values);
  };

  handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    clearTimeout(this.timer);

    const value = e.target.value;
    this.setState({ inputValue: value });

    if (value !== '' && this.props.onSearch) {
      this.timer = setTimeout(this.triggerSearch, 300);
    }
  };

  triggerSearch = () => {
    if (this.props.onSearch) {
      this.props.onSearch(this.state.inputValue);
    }
  };

  handleClickOnItem =
    (option: IOption) => (e: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
      if (!this.props.multiChoice) {
        e.stopPropagation();
      }
      this.handleAddOption(option);
      this.setState({ inputValue: '', focused: false, show: false });
    };

  handleAddOption = (option: IOption) => {
    let values = cloneDeep(this.props.values);
    if (this.props.selectionIndicator && this.props.multiChoice) {
      const tempValues = values.filter((item) => item.value !== option.value);
      if (tempValues.length === values.length) values.push(option);
      else values = tempValues;
    } else {
      if (values.find((it) => it.value === option.value)) return;
      if (this.props.multiChoice) {
        values.push(option);
      } else {
        values = [option];
      }
    }
    this.props.onChangeValues(values);
    // to update pointer
    const pointerIsOutOfOptionsLength =
      this.state.pointer ===
      this.props.options.length - this.props.values.length - 1;
    // to lose focus
    const isSingleChoice = !this.props.multiChoice;
    this.setState(({ focused, pointer }) => ({
      focused: isSingleChoice ? false : focused,
      pointer: pointerIsOutOfOptionsLength ? pointer - 1 : pointer,
      selectedChip: -1, // reset selectedChip
    }));
  };

  handleUpdatePointer =
    (index: number) => (_e: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
      this.setState({ pointer: index });
    };

  handleRemoveChip = (option: IOption) => {
    const values = this.props.values.filter(
      (item) => item.value !== option.value
    );
    this.props.onChangeValues(values);
  };

  handleRemoveValues = () => {
    this.props.onChangeValues([]);
  };

  onFocus = () => {
    if (!this.props.disabled) {
      this.setState({ focused: true });
      if (this.inputRef.current) {
        this.inputRef.current.focus();
      }
    }
  };

  render() {
    const {
      label,
      values,
      options,
      disabled,
      isRequired,
      placeholder,
      multiChoice,
      selectionIndicator,
      showValues,
      allowRemove,
      round,
      containerStyle,
      inputStyle,
      containerClass,
      fixMisPositionError,
      placement,
      target,
    } = this.props;
    const { expanded, show } = this.state;
    const expandable = new Set<number | string>();
    options.forEach((it) => {
      if (it.parent) {
        expandable.add(it.parent);
      }
    });

    return (
      <APopover
        arrow={this.props.arrow ?? true}
        curveBorder
        placement={placement}
        fixTop={fixMisPositionError ? 50 : undefined}
        show={show}
        onShowChange={(show) => {
          this.setState({ show: show });
        }}
        target={
          target || (
            <div style={containerStyle ?? {}} className={containerClass}>
              {label && <SelectLabel label={label} />}
              <Wrapper ref={this.wrapperRef} onClick={this.onFocus}>
                <InputContainer
                  focused={this.state.focused}
                  disabled={disabled}
                  round={round}
                  style={inputStyle}
                >
                  {multiChoice ? (
                    <ChipsWrapper>
                      {showValues &&
                        values.map((chip) => (
                          <Chip
                            key={chip.value}
                            chip={chip}
                            selected={this.state.selectedChip === chip.value}
                            onRemoveChip={this.handleRemoveChip}
                          />
                        ))}
                      <ChipsInput
                        id={label || this.inputId}
                        ref={this.inputRef}
                        name={label || this.inputId}
                        type="text"
                        value={this.state.inputValue}
                        values={this.props.values}
                        required={values.length === 0 && isRequired}
                        onChange={this.handleChangeInput}
                        className="browser-default"
                        placeholder={
                          placeholder ||
                          i18n.t('selectAnOption', { ns: 'general' })
                        }
                        autoComplete="off"
                        onKeyDown={this.handleActionsBasedOnKey}
                        onPaste={this.props.onPaste || this.onPaste}
                      />
                    </ChipsWrapper>
                  ) : (
                    <React.Fragment>
                      {values.length > 0 ? (
                        <SingleValue
                          error={
                            values.length > 0 &&
                            !options.find((it) => values[0].value === it.value)
                          }
                        >
                          {values[0].label}
                        </SingleValue>
                      ) : (
                        <ChipsInput
                          id={label || this.inputId}
                          ref={this.inputRef}
                          name={label || this.inputId}
                          required={isRequired}
                          autoComplete="off"
                          type="text"
                          value={this.state.inputValue}
                          onChange={this.handleChangeInput}
                          className="browser-default"
                          onKeyDown={this.handleActionsBasedOnKey}
                          placeholder={placeholder}
                          onPaste={this.props.onPaste}
                        />
                      )}
                    </React.Fragment>
                  )}

                  <SelectActionsIcon
                    showRemoveIcon={
                      allowRemove !== false &&
                      (showValues ? values.length > 0 : false)
                    }
                    onRemoveValues={this.handleRemoveValues}
                  />
                </InputContainer>
              </Wrapper>
            </div>
          )
        }
      >
        {options /* && this.state.focused*/ ? (
          <SelectOptions
            inPopOver={true}
            pointer={this.state.pointer}
            options={this.getFilteredOptions()}
            values={this.props.values}
            onUpdatePointer={this.handleUpdatePointer}
            onSelectAOption={this.handleClickOnItem}
            expanded={expanded}
            expandable={expandable}
            expand={this.expand}
            selectionIndicator={!!selectionIndicator}
            multiChoice={!!multiChoice}
          />
        ) : (
          <div />
        )}
      </APopover>
    );
  }
}

export default Select;
